import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, Sparkles, AlertTriangle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from "recharts";

export default function Forecast() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/forecast").then(r => setD(r.data)); }, []);
  if (!d) return <div className="p-10 text-zinc-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="forecast-page">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="h-6 w-6 text-violet-600" />
        <div>
          <h1 className="font-display text-3xl font-bold">AI Revenue Forecasting</h1>
          <p className="text-sm text-zinc-500 mt-1">Predicted by AI based on active pipeline</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-black/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Revenue Forecast</h3>
            <span className="text-xs text-violet-600 font-medium inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI-powered</span>
          </div>
          <p className="font-display text-4xl font-extrabold text-zinc-900">${Math.round(d.projected_revenue).toLocaleString()}</p>
          <p className="text-xs text-emerald-600 font-medium">+{d.growth_vs_last}% vs last month</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <AreaChart data={d.series}>
                <defs>
                  <linearGradient id="fa" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.5}/><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0}/></linearGradient>
                  <linearGradient id="fp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EC4899" stopOpacity={0.3}/><stop offset="100%" stopColor="#EC4899" stopOpacity={0}/></linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #e4e4e7", borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="actual" stroke="#8B5CF6" fill="url(#fa)" strokeWidth={2} />
                <Area type="monotone" dataKey="projected" stroke="#EC4899" fill="url(#fp)" strokeWidth={2} strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-3">AI Forecast Insights</h3>
          <ul className="space-y-3 text-sm text-zinc-700">
            {d.insights.map((i, idx) => (
              <li key={idx} className="flex gap-2"><Sparkles className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />{i}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Churn risk", value: d.churn_risk.toUpperCase(), color: "text-emerald-600" },
          { label: "Growth opportunity", value: `$${Math.round(d.growth_opportunity).toLocaleString()}`, color: "text-violet-600" },
          { label: "Launch forecast", value: `$${Math.round(d.launch_forecast).toLocaleString()}`, color: "text-pink-600" },
          { label: "Best performing", value: d.best_offer, color: "text-amber-600" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl bg-white border border-black/5 p-5">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{s.label}</p>
            <p className={`mt-1 font-display text-2xl font-extrabold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
